import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { getAnnouncementsData } from '../../data/announcements';
import { TimeSlot, generateLaterSlots } from './time';
import { useSession } from '../../data/session';
import type { UUID } from '../../data/types';

type Props = {
  open: boolean;
  parkId: UUID;
  parkName: string;
  onClose(): void;
  onPosted?(announcementId: UUID): void;
};

export function AnnounceSheet({
  open,
  parkId,
  parkName,
  onClose,
  onPosted,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<'choose' | 'confirm' | 'duplicate' | 'error'>('choose');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [posting, setPosting] = useState(false);
  const [lastAnnouncementId, setLastAnnouncementId] = useState<UUID | null>(null);
  const [duplicateTime, setDuplicateTime] = useState<Date | null>(null);
  const { familyDisplayName } = useSession();

  const laterSlots = useMemo(
    () => (open ? generateLaterSlots(new Date()) : []),
    [open]
  );

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      try {
        (dlg as any).showModal?.();
      } catch {
        // Fallback: set open attribute
        dlg.setAttribute('open', '');
      }
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  function close() {
    setMode('choose');
    setSelectedSlot(null);
    setPosting(false);
    setErrorMsg('');
    setDuplicateTime(null);
    onClose();
  }

  async function post(when: 'now' | Date) {
    setPosting(true);
    const data = getAnnouncementsData();
    const res = await data.createAnnouncement(parkId, when);
    setPosting(false);
    if (res.type === 'success') {
      setLastAnnouncementId(res.announcement.id);
      onPosted?.(res.announcement.id);
      try {
        window.dispatchEvent(new CustomEvent('headedout:posted', { detail: { id: res.announcement.id } }));
      } catch {
        // ignore
      }
      setMode('confirm');
    } else if (res.type === 'duplicate') {
      setDuplicateTime(res.previousCreatedAt ?? null);
      setMode('duplicate');
    } else {
      setErrorMsg("Couldn't post, try again");
      setMode('error');
    }
  }

  async function cancelNow() {
    if (!lastAnnouncementId) return;
    const data = getAnnouncementsData();
    const res = await data.cancelAnnouncement(lastAnnouncementId);
    if (res && res.type !== undefined) {
      setErrorMsg("Couldn't cancel, try again");
      setMode('error');
      return;
    }
    close();
  }

  return (
    <dialog class="sheet" ref={dialogRef} aria-label={`Announce at ${parkName}`}>
      <div class="sheet-body" role="document">
        {mode === 'choose' && (
          <div class="sheet-section">
            <h3 class="sheet-title">Heading to {parkName}</h3>
            <div class="sheet-actions">
              <button
                class="btn-primary"
                type="button"
                disabled={posting}
                onClick={() => post('now')}
                aria-label={`Announce now at ${parkName}`}
              >
                Now
              </button>
              {laterSlots.length > 0 && (
                <div class="later">
                  <label class="later-label" for="later-time">
                    Later today
                  </label>
                  <select
                    id="later-time"
                    class="later-select"
                    onChange={(e) => {
                      const idx = (e.target as HTMLSelectElement).value;
                      const slot = laterSlots[Number(idx)];
                      setSelectedSlot(slot);
                    }}
                  >
                    {laterSlots.map((s, i) => (
                      <option value={i} key={i}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <button
                    class="btn-secondary"
                    type="button"
                    disabled={posting}
                    onClick={() => {
                      const slot = selectedSlot ?? laterSlots[0];
                      if (slot) post(slot.at);
                    }}
                    aria-label={`Announce later at ${selectedSlot?.label ?? laterSlots[0]?.label ?? ''}`}
                  >
                    Post
                  </button>
                </div>
              )}
            </div>
            <div class="sheet-row">
              <button class="btn-plain" type="button" onClick={close} aria-label="Close">
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === 'confirm' && (
          <div class="sheet-section">
            <p class="confirm-text">
              Posted: {familyDisplayName} are heading to {parkName}
              {selectedSlot ? ` at ${selectedSlot.label}` : ''}
            </p>
            <div class="sheet-actions">
              <button class="btn-danger" type="button" onClick={cancelNow}>
                Cancel
              </button>
              <button class="btn-plain" type="button" onClick={close}>
                Done
              </button>
            </div>
          </div>
        )}

        {mode === 'duplicate' && (
          <div class="sheet-section">
            <p class="error-text">
              You already posted {parkName}{' '}
              {duplicateTime ? 'a few minutes ago' : 'recently'}.
            </p>
            {duplicateTime && (
              <p class="muted">
                Last post at{' '}
                {new Intl.DateTimeFormat('en-US', {
                  timeZone: 'America/Denver',
                  hour: 'numeric',
                  minute: '2-digit',
                }).format(duplicateTime)}
              </p>
            )}
            <div class="sheet-row">
              <button class="btn-plain" type="button" onClick={close}>
                OK
              </button>
            </div>
          </div>
        )}

        {mode === 'error' && (
          <div class="sheet-section">
            <p class="error-text">{errorMsg || "Couldn't post, try again"}</p>
            <div class="sheet-row">
              <button class="btn-plain" type="button" onClick={close}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </dialog>
  );
}

